import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Handshake, ArrowsClockwise, Trophy, ArrowRight } from "@phosphor-icons/react";

const RANGES = [
  { key: 7, label: "Last 7 days" },
  { key: 14, label: "Last 14 days" },
  { key: 30, label: "Last 30 days" },
];

const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
};

const relTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

export default function CrmActivityCard() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    (async () => {
      try {
        const { data } = await api.get(`/crm/recent-activity?days=${days}`);
        if (!cancelled) setData(data);
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [days]);
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="crm-activity-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="label-micro">CRM · Live activity</div>
          <h2 className="font-display font-bold text-slate-900 mt-0.5">Recent lead activity</h2>
        </div>
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden">
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setDays(r.key)}
                    data-testid={`crm-activity-range-${r.key}`}
                    className={`text-xs font-semibold px-2.5 py-1.5 transition-colors ${days === r.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Tile icon={Handshake} tone="blue" label="New leads" value={data?.new_leads ?? (busy ? "…" : 0)}/>
        <Tile icon={ArrowsClockwise} tone="amber" label="Stage moves" value={data?.stage_movements ?? (busy ? "…" : 0)}/>
        <Tile icon={Trophy} tone="emerald" label="Closed-won" value={data?.won ?? (busy ? "…" : 0)}/>
      </div>
      {(data?.recent || []).length > 0 && (
        <div className="mt-5">
          <div className="label-micro mb-2">Most recent movements</div>
          <ul className="space-y-2">
            {(data.recent).map(r => (
              <li key={r.hs_id} className="flex items-center justify-between gap-3 border-b border-slate-100 last:border-0 pb-2 last:pb-0" data-testid={`crm-activity-row-${r.hs_id}`}>
                <div className="min-w-0 flex-1">
                  <Link to={`/crm?deal=${r.hs_id}`} className="font-semibold text-blue-700 hover:underline text-sm truncate block">{r.name}</Link>
                  <div className="text-[11px] text-slate-500 truncate">{r.owner_name} · <span className="tabular-nums">{fmtINR(r.amount)}</span> · <span className="font-semibold">{r.stage_label}</span></div>
                </div>
                <span className="text-[11px] text-slate-400 shrink-0">{relTime(r.at)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 text-right">
            <Link to="/crm" className="text-xs font-semibold text-blue-700 hover:underline inline-flex items-center gap-0.5">Open CRM <ArrowRight size={11}/></Link>
          </div>
        </div>
      )}
      {!busy && data && data.new_leads === 0 && data.stage_movements === 0 && (
        <div className="mt-4 text-xs text-slate-500 italic">No new leads or stage movements in this window. Try a wider range.</div>
      )}
    </section>
  );
}

function Tile({ icon: Icon, tone, label, value }) {
  const tones = { blue: "bg-blue-100 text-blue-700", amber: "bg-amber-100 text-amber-700", emerald: "bg-emerald-100 text-emerald-700" };
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-6 h-6 rounded-md grid place-items-center ${tones[tone]}`}><Icon size={12} weight="fill"/></div>
        <div className="label-micro">{label}</div>
      </div>
      <div className="font-display font-black text-2xl text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}
